import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

/**
 * A search result with position and source information
 */
export interface SearchResult {
  /** Start position within the source */
  from: number;
  /** End position within the source */
  to: number;
  /** ID of the searchable source */
  sourceId: string;
  /** Display name of the source */
  sourceName: string;
  /** The matched text */
  matchText: string;
}

/**
 * A match position without source metadata
 */
export interface MatchPosition {
  from: number;
  to: number;
}

/**
 * A cached text segment from a ProseMirror document.
 * Pre-extracted to avoid walking the PM tree on every search.
 */
export interface TextSegment {
  /** The text content of this node */
  text: string;
  /** ProseMirror position of the start of this text node */
  pmPos: number;
}

/**
 * Extracts all text segments from a ProseMirror document.
 * This is the "write" side of the text cache — called once on content change.
 */
export function extractTextSegments(doc: ProseMirrorNode): TextSegment[] {
  const segments: TextSegment[] = [];
  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      segments.push({ text: node.text, pmPos: pos });
    }
  });
  return segments;
}

/**
 * Searches pre-extracted text segments using a regex.
 * This is the "read" side of the text cache — fast because it skips PM tree walking.
 */
export function searchTextSegments(
  segments: TextSegment[],
  regex: RegExp,
  sourceId: string,
  sourceName: string
): SearchResult[] {
  const results: SearchResult[] = [];
  for (const segment of segments) {
    const matches = Array.from(segment.text.matchAll(regex)).filter(([matchText]) => matchText.trim());
    for (const m of matches) {
      if (m[0] === "" || m.index === undefined) continue;
      results.push({
        from: segment.pmPos + m.index,
        to: segment.pmPos + m.index + m[0].length,
        sourceId,
        sourceName,
        matchText: m[0],
      });
    }
  }
  return results;
}

/**
 * Creates a case-insensitive regex from a search term, escaping special characters.
 * @param term - The search term
 * @param flags - Regex flags (defaults to "gui" for global, unicode, case-insensitive)
 * @returns The regex or null if invalid
 */
export function createSearchRegex(term: string, flags: string = "gui"): RegExp | null {
  if (!term) return null;
  try {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(escaped, flags);
  } catch {
    return null;
  }
}

/**
 * Searches a TipTap/ProseMirror document for matches of the given term.
 * This is a shared utility used by both useTiptapSearchable and useTiptapFieldSearchable.
 *
 * @param doc - The ProseMirror document node
 * @param term - The search term
 * @param sourceId - ID to tag results with
 * @param sourceName - Display name to tag results with
 * @returns Array of search results
 */
export function searchTiptapDoc(
  doc: ProseMirrorNode | null | undefined,
  term: string,
  sourceId: string,
  sourceName: string
): SearchResult[] {
  const regex = createSearchRegex(term);
  if (!regex || !doc) return [];

  const results: SearchResult[] = [];

  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      const text = node.text;
      const matches = Array.from(text.matchAll(regex)).filter(([matchText]) => matchText.trim());

      for (const m of matches) {
        if (m[0] === "" || m.index === undefined) continue;

        results.push({
          from: pos + m.index,
          to: pos + m.index + m[0].length,
          sourceId,
          sourceName,
          matchText: m[0],
        });
      }
    }
  });

  return results;
}

/**
 * Collects all match positions in a TipTap/ProseMirror document for replace operations.
 * Returns positions only (no metadata) sorted for reverse-order replacement.
 *
 * @param doc - The ProseMirror document node
 * @param term - The search term
 * @returns Array of positions sorted descending by 'from'
 */
export function collectTiptapMatchPositions(
  doc: ProseMirrorNode | null | undefined,
  term: string
): MatchPosition[] {
  const regex = createSearchRegex(term);
  if (!regex || !doc) return [];

  const positions: MatchPosition[] = [];

  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      const text = node.text;
      const matches = Array.from(text.matchAll(regex)).filter(([matchText]) => matchText.trim());

      for (const m of matches) {
        if (m[0] === "" || m.index === undefined) continue;
        positions.push({
          from: pos + m.index,
          to: pos + m.index + m[0].length,
        });
      }
    }
  });

  // Sort descending by position so replacements don't invalidate earlier positions
  return positions.sort((a, b) => b.from - a.from);
}

/**
 * Measures the Y offset of a character position within a textarea.
 * Creates a hidden measurement element that mirrors the textarea's styling
 * to accurately account for word wrapping.
 *
 * @param textarea - The textarea element
 * @param charPosition - The character index to measure to
 * @returns The Y offset in pixels from the top of the textarea
 */
export function measureTextareaOffset(
  textarea: HTMLTextAreaElement,
  charPosition: number
): number {
  const style = getComputedStyle(textarea);

  // Create a hidden div that mirrors the textarea's text rendering
  const mirror = document.createElement("div");
  mirror.style.cssText = `
    position: absolute;
    top: -9999px;
    left: -9999px;
    visibility: hidden;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    width: ${textarea.clientWidth}px;
    font-family: ${style.fontFamily};
    font-size: ${style.fontSize};
    font-weight: ${style.fontWeight};
    line-height: ${style.lineHeight};
    letter-spacing: ${style.letterSpacing};
    padding: ${style.padding};
    border: ${style.border};
    box-sizing: ${style.boxSizing};
  `;

  // Get text up to the match position
  const textBeforeMatch = textarea.value.substring(0, charPosition);

  // Use a span to mark the end position so we can measure to it
  mirror.innerHTML =
    escapeHtml(textBeforeMatch) + '<span id="measure-marker"></span>';

  document.body.appendChild(mirror);

  // Measure the position of the marker
  const marker = mirror.querySelector("#measure-marker");
  const offset = marker ? marker.offsetTop : 0;

  // Clean up
  document.body.removeChild(mirror);

  return offset;
}

// Helper to escape HTML for the measurement div
function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Finds the scrollable ancestor of an element.
 * Walks up the DOM tree (in the element's document) for overflow: auto/scroll.
 * If the element is inside an iframe with no inner scroller, continues from the frame
 * in the parent document so the Write view center column (`.editor-stack`) can be found.
 *
 * Important: do not return an unrelated `document.querySelector(".editor-stack")` — the
 * manuscript editor DOM often lives in a nested document; pairing it with the outer stack
 * made `getElementOffsetInContainer` always compute 0 and jumps reported `ok` without scrolling.
 */
export function findScrollContainer(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;

  const doc = element.ownerDocument;
  const win = doc.defaultView;
  if (!win) return null;

  let current: HTMLElement | null = element;
  while (current) {
    const style = win.getComputedStyle(current);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return current;
    }
    current = current.parentElement;
  }

  return null;
}

/** Walks the offsetParent chain from element to the document root, summing offsetTops. */
function getAbsoluteTopInDocument(element: HTMLElement): number {
  let top = 0;
  let current: HTMLElement | null = element;
  while (current) {
    top += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }
  return top;
}

/**
 * Gets the absolute offset of an element within a scroll container.
 * Walks up the offsetParent chain to calculate the total offset.
 * This works reliably for both on-screen and off-screen elements.
 */
function getElementOffsetInContainer(
  element: HTMLElement,
  scrollContainer: HTMLElement
): number {
  let offset = 0;
  let current: HTMLElement | null = element;

  while (current && current !== scrollContainer && scrollContainer.contains(current)) {
    offset += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }

  return offset;
}

/**
 * Smoothly animates a scroll container's scrollTop from its current value to targetTop.
 * Uses a JS rAF loop + ease-in-out curve so the animation works regardless of whether the
 * container has `overflow-y: auto/scroll` in CSS (native `scrollTo({ behavior: "smooth" })`
 * is unreliable on containers whose CSS overflow is "visible" but that still scroll).
 */
export function smoothScrollTop(container: HTMLElement, targetTop: number, duration = 380): void {
  const startTop = container.scrollTop;
  const delta = targetTop - startTop;
  if (Math.abs(delta) < 2) return; // already at target

  const startTime = performance.now();

  function easeInOut(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  function step(now: number): void {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / duration, 1);
    container.scrollTop = startTop + delta * easeInOut(t);
    if (t < 1) requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}

/**
 * Scrolls to center a specific position within an element.
 * Uses the same approach as the Document editor: calculate absolute position
 * in scroll container and directly set scrollTop.
 *
 * @param element - The element containing the match
 * @param offsetWithinElement - The Y offset of the match within the element (0 = top of element)
 * @param scrollContainer - The scrollable container element
 * @param behavior - Scroll behavior: "instant" (default) or "smooth"
 */
export function scrollToCenterElement(
  element: HTMLElement | null,
  offsetWithinElement: number,
  scrollContainer: HTMLElement | null,
  behavior: ScrollBehavior = "instant",
): void {
  if (!element || !scrollContainer) return;

  // Cross-document (legacy iframe) guard: the manuscript editor now lives in the
  // main document, so this branch is only reachable for other embedded frames.
  if (element.ownerDocument !== scrollContainer.ownerDocument) {
    const iframeWin = element.ownerDocument.defaultView;
    const frame = iframeWin?.frameElement as HTMLElement | null;
    if (frame && scrollContainer.contains(frame)) {
      // iframe's absolute top within the scroll container's content
      const frameTopInContainer = getElementOffsetInContainer(frame, scrollContainer);
      // element's absolute top within the iframe content (+ iframe's own window scroll, if any)
      const iframeScrollY = iframeWin?.scrollY ?? 0;
      const elementTopInIframe = getAbsoluteTopInDocument(element) + iframeScrollY;
      const absolutePosition = frameTopInContainer + elementTopInIframe + offsetWithinElement;
      const targetTop = Math.max(0, absolutePosition + element.offsetHeight / 2 - scrollContainer.clientHeight / 2);
      if (behavior === "smooth") {
        smoothScrollTop(scrollContainer, targetTop);
      } else {
        scrollContainer.scrollTop = targetTop;
      }
      return;
    }
    try {
      element.scrollIntoView({ block: "center", inline: "nearest", behavior });
    } catch {
      // non-fatal
    }
    return;
  }

  // Get the element's absolute position in the scroll container's content
  // This mirrors how the Document editor calculates position
  const elementOffset = getElementOffsetInContainer(element, scrollContainer);

  // Add the offset within the element to get the match's absolute position
  const matchPosition = elementOffset + offsetWithinElement;

  // Center the result in the viewport (same formula as Document editor)
  const targetTop = Math.max(0, matchPosition - scrollContainer.clientHeight / 2);
  if (behavior === "smooth") {
    smoothScrollTop(scrollContainer, targetTop);
  } else {
    scrollContainer.scrollTop = targetTop;
  }
}
